export type ComposerInlineToken =
  | {
      readonly type: "mention";
      readonly value: string;
      readonly source: string;
      readonly start: number;
      readonly end: number;
    }
  | {
      readonly type: "skill";
      readonly value: string;
      readonly source: string;
      readonly start: number;
      readonly end: number;
    }
  | {
      /**
       * An org knowledge-base document. `value` is the document id (what the
       * agent fetches with); `label` is the filename a human reads. It needs
       * its own token because the file-link tokenizer rejects it twice over —
       * `policy:` is a URI scheme, and the label is not the destination's
       * basename.
       */
      readonly type: "policy";
      readonly value: string;
      readonly label: string;
      readonly source: string;
      readonly start: number;
      readonly end: number;
    }
  | {
      /**
       * One of the person's connected apps (`personal`) or a workspace
       * integration (`workspace`). `value` is the connector id the plugin
       * tools take; `label` is the app's name.
       */
      readonly type: "connection";
      readonly value: string;
      readonly scope: "personal" | "workspace";
      readonly label: string;
      readonly source: string;
      readonly start: number;
      readonly end: number;
    };

export interface CollectComposerInlineTokensOptions {
  readonly preserveTrailingFrom?: ReadonlyArray<ComposerInlineToken>;
}

const MAX_FILE_LINK_LABEL_LENGTH = 512;
const MAX_INLINE_TOKEN_LENGTH = 4_096;
/** `[Some Policy.pdf](policy:doc-123)` — same label bound as a file link, same reasoning. */
const POLICY_LINK_TOKEN_REGEX = new RegExp(
  `(^|\\s)\\[((?:\\\\.|[^\\]\\\\]){0,${MAX_FILE_LINK_LABEL_LENGTH}})\\]\\(policy:([^)\\s]+)\\)(?=\\s)`,
  "g",
);
/** `[Gmail](connection:personal/gmail)` — the same shape as a policy link, with the scope in the path. */
const CONNECTION_LINK_TOKEN_REGEX = new RegExp(
  `(^|\\s)\\[((?:\\\\.|[^\\]\\\\]){0,${MAX_FILE_LINK_LABEL_LENGTH}})\\]\\(connection:(personal|workspace)/([^)\\s]+)\\)(?=\\s)`,
  "g",
);
const URI_SCHEME_REGEX = /^[A-Za-z][A-Za-z0-9+.-]*:/;
const WINDOWS_DRIVE_PATH_REGEX = /^[A-Za-z]:[\\/]/;

function isWhitespace(character: string | undefined): boolean {
  return character !== undefined && character.trim().length === 0;
}

function hasTokenBoundary(text: string, index: number): boolean {
  return index === 0 || isWhitespace(text[index - 1]);
}

function unescapeBackslashPairs(value: string): string {
  let result = "";
  for (let index = 0; index < value.length; index += 1) {
    if (value[index] === "\\" && index + 1 < value.length) {
      index += 1;
    }
    result += value[index] ?? "";
  }
  return result;
}

// Autocomplete emits canonical file links, so ambiguous bare @scope/package text stays a package.
function isScopedPackageReference(value: string): boolean {
  const isPackageStart = (character: string | undefined) =>
    character !== undefined &&
    ((character >= "a" && character <= "z") || (character >= "0" && character <= "9"));
  const isPackageCharacter = (character: string | undefined) =>
    isPackageStart(character) ||
    (character !== undefined && (character === "." || character === "_" || character === "-"));
  const hasValidSegment = (start: number, end: number) => {
    if (start >= end || !isPackageStart(value[start])) {
      return false;
    }
    for (let index = start + 1; index < end; index += 1) {
      if (!isPackageCharacter(value[index])) {
        return false;
      }
    }
    return true;
  };

  const firstSlash = value.indexOf("/");
  if (firstSlash < 0) {
    return false;
  }
  const secondSlash = value.indexOf("/", firstSlash + 1);
  const packageEnd = secondSlash < 0 ? value.length : secondSlash;
  if (!hasValidSegment(0, firstSlash) || !hasValidSegment(firstSlash + 1, packageEnd)) {
    return false;
  }
  if (secondSlash < 0) {
    return true;
  }
  if (secondSlash === value.length - 1) {
    return false;
  }
  for (let index = secondSlash + 1; index < value.length; index += 1) {
    const character = value[index];
    if (character === "@" || character === '"' || isWhitespace(character)) {
      return false;
    }
  }
  return true;
}

function collectFileLinkTokens(text: string): ComposerInlineToken[] {
  const matches: ComposerInlineToken[] = [];

  for (let start = 0; start < text.length; start += 1) {
    if (text[start] !== "[" || !hasTokenBoundary(text, start)) {
      continue;
    }

    let cursor = start + 1;
    let labelUnits = 0;
    while (cursor < text.length && labelUnits <= MAX_FILE_LINK_LABEL_LENGTH) {
      if (text[cursor] === "\\" && cursor + 1 < text.length) {
        cursor += 2;
        labelUnits += 1;
        continue;
      }
      if (text[cursor] === "]") {
        break;
      }
      cursor += 1;
      labelUnits += 1;
    }
    if (
      labelUnits > MAX_FILE_LINK_LABEL_LENGTH ||
      text[cursor] !== "]" ||
      text[cursor + 1] !== "("
    ) {
      continue;
    }

    const label = unescapeBackslashPairs(text.slice(start + 1, cursor));
    const pathStart = cursor + 2;
    cursor = pathStart;
    while (
      cursor < text.length &&
      cursor - pathStart <= MAX_INLINE_TOKEN_LENGTH &&
      text[cursor] !== ")" &&
      !isWhitespace(text[cursor])
    ) {
      cursor += 1;
    }
    if (
      cursor === pathStart ||
      cursor - pathStart > MAX_INLINE_TOKEN_LENGTH ||
      text[cursor] !== ")" ||
      !isWhitespace(text[cursor + 1])
    ) {
      continue;
    }

    const encodedPath = text.slice(pathStart, cursor);
    let path = encodedPath;
    try {
      path = decodeURIComponent(encodedPath);
    } catch {
      // Preserve malformed source rather than dropping a user-authored token.
    }
    const separatorIndex = Math.max(path.lastIndexOf("/"), path.lastIndexOf("\\"));
    const basename = separatorIndex >= 0 ? path.slice(separatorIndex + 1) : path;
    const hasExternalScheme = URI_SCHEME_REGEX.test(path) && !WINDOWS_DRIVE_PATH_REGEX.test(path);
    if (!path || hasExternalScheme || label !== basename) {
      continue;
    }
    const end = cursor + 1;
    matches.push({
      type: "mention",
      value: path,
      source: text.slice(start, end),
      start,
      end,
    });
  }

  return matches;
}

function collectAtMentionTokens(text: string): ComposerInlineToken[] {
  const matches: ComposerInlineToken[] = [];

  for (let start = 0; start < text.length; start += 1) {
    if (text[start] !== "@" || !hasTokenBoundary(text, start)) {
      continue;
    }

    const quoted = text[start + 1] === '"';
    const pathStart = start + (quoted ? 2 : 1);
    let cursor = pathStart;
    if (quoted) {
      while (cursor < text.length && cursor - pathStart <= MAX_INLINE_TOKEN_LENGTH) {
        if (text[cursor] === "\\" && cursor + 1 < text.length) {
          cursor += 2;
          continue;
        }
        if (text[cursor] === '"') {
          break;
        }
        cursor += 1;
      }
    } else {
      while (
        cursor < text.length &&
        cursor - pathStart <= MAX_INLINE_TOKEN_LENGTH &&
        text[cursor] !== "@" &&
        text[cursor] !== '"' &&
        !isWhitespace(text[cursor])
      ) {
        cursor += 1;
      }
    }

    const isTerminated = quoted ? text[cursor] === '"' : isWhitespace(text[cursor]);
    const end = quoted ? cursor + 1 : cursor;
    if (
      cursor === pathStart ||
      cursor - pathStart > MAX_INLINE_TOKEN_LENGTH ||
      !isTerminated ||
      !isWhitespace(text[end])
    ) {
      continue;
    }

    const sourcePath = text.slice(pathStart, cursor);
    const path = quoted ? unescapeBackslashPairs(sourcePath) : sourcePath;
    if (!path) {
      continue;
    }
    if (!quoted && isScopedPackageReference(path)) {
      continue;
    }
    matches.push({
      type: "mention",
      value: path,
      source: text.slice(start, end),
      start,
      end,
    });
  }

  return matches;
}

function collectSkillTokens(text: string): ComposerInlineToken[] {
  const matches: ComposerInlineToken[] = [];
  const isSkillStart = (character: string | undefined) =>
    character !== undefined &&
    ((character >= "a" && character <= "z") || (character >= "A" && character <= "Z"));
  const isSkillContinuation = (character: string | undefined) =>
    isSkillStart(character) ||
    (character !== undefined && character >= "0" && character <= "9") ||
    character === ":" ||
    character === "_" ||
    character === "-";

  for (let start = 0; start < text.length; start += 1) {
    if (text[start] !== "$" || !hasTokenBoundary(text, start) || !isSkillStart(text[start + 1])) {
      continue;
    }
    let end = start + 2;
    while (end - start <= MAX_INLINE_TOKEN_LENGTH && isSkillContinuation(text[end])) {
      end += 1;
    }
    if (end - start > MAX_INLINE_TOKEN_LENGTH || !isWhitespace(text[end])) {
      continue;
    }
    matches.push({
      type: "skill",
      value: text.slice(start + 1, end),
      source: text.slice(start, end),
      start,
      end,
    });
  }

  return matches;
}

/**
 * Org knowledge-base documents: `[label](policy:<id>)`. The file-link
 * tokenizer rejects these twice over (`policy:` is a URI scheme, and the
 * label is not the destination's basename), so they get their own pass.
 */
function collectPolicyLinkTokens(text: string): ComposerInlineToken[] {
  const matches: ComposerInlineToken[] = [];
  for (const match of text.matchAll(POLICY_LINK_TOKEN_REGEX)) {
    const fullMatch = match[0];
    const prefix = match[1] ?? "";
    const label = (match[2] ?? "").replace(/\\(.)/g, "$1");
    const encodedId = match[3] ?? "";
    if (!label || !encodedId) {
      continue;
    }
    let policyId = encodedId;
    try {
      policyId = decodeURIComponent(encodedId);
    } catch {
      // Keep the malformed source rather than dropping the user's token.
    }
    const start = (match.index ?? 0) + prefix.length;
    const end = start + fullMatch.length - prefix.length;
    matches.push({
      type: "policy",
      value: policyId,
      label,
      source: text.slice(start, end),
      start,
      end,
    });
  }
  return matches;
}

/** Connected apps and workspace integrations: `[label](connection:<scope>/<connector id>)`. */
function collectConnectionLinkTokens(text: string): ComposerInlineToken[] {
  const matches: ComposerInlineToken[] = [];
  for (const match of text.matchAll(CONNECTION_LINK_TOKEN_REGEX)) {
    const fullMatch = match[0];
    const prefix = match[1] ?? "";
    const label = (match[2] ?? "").replace(/\\(.)/g, "$1");
    const scope = match[3] === "workspace" ? "workspace" : "personal";
    const encodedId = match[4] ?? "";
    if (!label || !encodedId) {
      continue;
    }
    let connectorId = encodedId;
    try {
      connectorId = decodeURIComponent(encodedId);
    } catch {
      // Keep the malformed source rather than dropping the user's token.
    }
    const start = (match.index ?? 0) + prefix.length;
    const end = start + fullMatch.length - prefix.length;
    matches.push({
      type: "connection",
      value: connectorId,
      scope,
      label,
      source: text.slice(start, end),
      start,
      end,
    });
  }
  return matches;
}

export function collectComposerInlineTokens(
  text: string,
  options: CollectComposerInlineTokensOptions = {},
): ReadonlyArray<ComposerInlineToken> {
  const matches = [
    ...collectFileLinkTokens(text),
    ...collectPolicyLinkTokens(text),
    ...collectConnectionLinkTokens(text),
    ...collectAtMentionTokens(text),
    ...collectSkillTokens(text),
  ];

  for (const token of options.preserveTrailingFrom ?? []) {
    if (
      token.end === text.length &&
      text.slice(token.start, token.end) === token.source &&
      !matches.some(
        (match) =>
          match.type === token.type && match.start === token.start && match.end === token.end,
      )
    ) {
      matches.push(token);
    }
  }

  return [...matches].sort((left, right) => left.start - right.start);
}
